import React from 'react';
import classNames from 'classnames';
import { NavLink } from 'react-router-dom';
import FontAwesomeIcon from 'src/components/Icon';
import Skeleton from 'src/components/Skeleton/Skeleton';
import './SidebarContextTree.scss';

export interface SidebarContextRow {
  label: string;
  path: string;
  icon: string;
  depth?: number;
  loading?: boolean;
}

interface SidebarContextTreeProps {
  rows: SidebarContextRow[];
}

const MAX_LABEL_LENGTH: number = 20;
const SKELETON_WIDTH_BY_DEPTH: string[] = ['8rem', '5rem', '6rem'];

const truncateLabel = (label: string): string => {
  if (label.length <= MAX_LABEL_LENGTH) {
    return label;
  }

  return `${label.slice(0, MAX_LABEL_LENGTH)}…`;
};

const SidebarContextTree = ({ rows }: SidebarContextTreeProps) => {
  return (
    <ul className="sidebar-context-tree">
      {rows.map((row: SidebarContextRow, index: number) => {
        const isCurrent: boolean = index === rows.length - 1;

        const linkClassName: string = classNames(
          'sidebar-context-tree__link',
          'sidebar-context-tree__link--active',
          {
            [`sidebar-context-tree__link--depth-${row.depth}`]: row.depth,
            'sidebar-context-tree__link--current': isCurrent,
          }
        );

        const content: React.ReactNode = (
          <>
            <FontAwesomeIcon weight="light" glyph={row.icon} />
            {row.loading ? (
              <Skeleton
                variant="text"
                width={SKELETON_WIDTH_BY_DEPTH[row.depth ?? 0] ?? '6rem'}
                className="sidebar-context-tree__skeleton"
              />
            ) : (
              <span className="sidebar-context-tree__label">
                {truncateLabel(row.label)}
              </span>
            )}
          </>
        );

        return (
          <li className="sidebar-context-tree__row" key={row.path}>
            {isCurrent ? (
              <span className={linkClassName} aria-current="page">
                {content}
              </span>
            ) : (
              <NavLink to={row.path} className={linkClassName}>
                {content}
              </NavLink>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default SidebarContextTree;
